import { AxiosError } from 'axios'
import { axiosClient } from '../../clients/axios-client'
import { APP_VERSION, TELEMETRY_ENABLED } from '../../constants/telemetry'
import type { TelemetryTaskType } from '../../types/telemetry'
import { ChatCompletionHttpError } from './chat-completion'
import { TelemetryDocumentDtoSchema, TelemetryEventDtoSchema } from './dtos/telemetry'

export type TelemetryFailureType = 'timeout' | 'network' | 'api_error' | 'word_error' | 'other'

interface TaskFields {
  task_id: string
  task_type: TelemetryTaskType
  model_version?: string
}

export type TelemetryEventInput =
  | (TaskFields & { event_type: 'task_started' })
  | (TaskFields & {
      event_type: 'task_succeeded'
      task_outcome: 'responded' | 'comments_applied'
      duration_ms: number
    })
  | (TaskFields & {
      event_type: 'task_failed'
      failure_type: TelemetryFailureType
      duration_ms: number
    })
  | (TaskFields & {
      event_type: 'content_inserted'
      insertion_id: string
      characters_inserted: number
    })
  | (TaskFields & {
      event_type: 'insertion_checked'
      insertion_id: string
      characters_remaining: number
      document_characters: number
    })

function warnEventNotSent(error: unknown) {
  console.warn('[Telemetry] event not sent:', error)
}

/**
 * Sends one anonymous behaviour event to POST /telemetry without waiting for it.
 *
 * It never throws and never rejects. Telemetry must not slow down or break chat,
 * so every failure ends in a console warning and nothing else.
 */
export function sendTelemetryEvent(event: TelemetryEventInput): void {
  if (!TELEMETRY_ENABLED) return

  try {
    const body = TelemetryEventDtoSchema.parse({
      schema_version: '1.0',
      event_id: crypto.randomUUID(),
      occurred_at: new Date().toISOString(),
      app_version: APP_VERSION,
      ...event,
    })
    axiosClient.post('/telemetry', body).catch(warnEventNotSent)
  } catch (error) {
    warnEventNotSent(error)
  }
}

export type TelemetryDocumentSource = 'word' | 'upload' | 'brave'

function decodeOrKeep(text: string): string {
  try {
    return decodeURIComponent(text)
  } catch {
    return text
  }
}

/**
 * Keeps folders and file name, and drops who the document belongs to.
 *
 * A SharePoint URL loses its host, its query string (which carries login tokens) and the
 * OneDrive owner: ".../personal/user_example_bayer_com/Documents/a.docx?sdata=..." becomes
 * "personal/Documents/a.docx". A local path loses the user folder: "C:\Users\user\Desktop\a.docx"
 * becomes "local/Desktop/a.docx", case-insensitively so "C:\users\..." is caught too. A macOS
 * temp path for an unsaved document loses its per-user token the same way. The backend repeats
 * this before saving.
 */
function stripUserFromPath(rawPath: string): string {
  const trimmed = rawPath.trim()
  const isUrl = /^https?:\/\//i.test(trimmed)

  const path = isUrl
    ? decodeOrKeep(new URL(trimmed).pathname).replace(/^\/personal\/[^/]+/, 'personal')
    : trimmed
        .replace(/\\/g, '/')
        // Windows paths are case-insensitive, so "C:\users\..." is as common as "C:\Users\...".
        .replace(/^([a-zA-Z]:)?\/?Users\/[^/]+/i, 'local')
        // macOS gives unsaved documents a path through a per-user temp directory, e.g.
        // "/var/folders/zq/6np4n6lx5p30z95_bmmqnvnc0000gp/T/Word add-in ....docx". The second
        // segment is a token tied to the machine's user, so it's stripped the same way as "Users".
        .replace(/^\/?var\/folders\/[^/]+\/[^/]+/i, 'local')

  return path.replace(/^\/+|\/+$/g, '')
}

/**
 * Tells POST /telemetry/document which document Scribbler was used on, without waiting for it.
 *
 * The backend keeps one row per path per week, so calling this again for the same document
 * is harmless. Like sendTelemetryEvent it never throws: telemetry must not break the add-in.
 */
export function sendTelemetryDocument(source: TelemetryDocumentSource, rawPath: string): void {
  if (!TELEMETRY_ENABLED) return

  try {
    const path = stripUserFromPath(rawPath)
    if (!path) return

    const body = TelemetryDocumentDtoSchema.parse({ schema_version: '1.0', source, path })
    axiosClient.post('/telemetry/document', body).catch(warnEventNotSent)
  } catch (error) {
    warnEventNotSent(error)
  }
}

// fetch() rejects with a TypeError when the server can't be reached at all.
// Chromium says "Failed to fetch", WebKit (Word on Mac) says "Load failed".
const FETCH_NETWORK_ERROR = /failed to fetch|load failed|networkerror/i

export function failureTypeFromError(error: unknown): TelemetryFailureType {
  if (error instanceof AxiosError) {
    const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
    if (timedOut) return 'timeout'
    return error.response ? 'api_error' : 'network'
  }

  if (error instanceof ChatCompletionHttpError) return 'api_error'

  const isFetchNetworkError = error instanceof TypeError && FETCH_NETWORK_ERROR.test(error.message)
  if (isFetchNetworkError) return 'network'

  // Office.js failures are OfficeExtension.Error instances, which carry this name.
  const isOfficeJsError = error instanceof Error && error.name === 'RichApi.Error'
  if (isOfficeJsError) return 'word_error'

  return 'other'
}
